import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { fetchTransactions } from "../services/transactionService.js";

export const useTransactions = () => {
  const token = localStorage.getItem("access_token");
  const apiUrl = process.env.REACT_APP_API_URL_DEV + '/api/v1/linkstransactions';
  const itemsPerPage = 20;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtros, setFiltros] = useState({
    status: "",
    bank: "",
    method: "",
    ultimos4: "",
    transaccionId: "",
    fechaInicio: "",
    horaInicio: "",
    fechaFin: "",
    horaFin: "",
  });
  const [showApproved, setShowApproved] = useState(true);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // --- OBTENCIÓN DE DATOS ---
  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      setError(null);
      try {
        const transactions = await fetchTransactions(apiUrl, token);
        setData(transactions);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [token, apiUrl]);

  const memoizedData = useMemo(() => {
    const filtered = data
      .filter(item => {

        if (!filtros.status) return true;

        const itemStatus = String(item.status || "").toLowerCase().trim();
        const filterStatus = String(filtros.status).toLowerCase().trim();

        const approvedGroup = ["approvedtransaction", "aprobada", "approved"];
        if (approvedGroup.includes(filterStatus)) {
          return approvedGroup.includes(itemStatus);
        }

        const declinedGroup = ["declinedtransaction", "declined_transaction", "declined", "rechazada", "failed", "error", "rejected"];
        if (declinedGroup.includes(filterStatus)) {
          return declinedGroup.includes(itemStatus) || !approvedGroup.includes(itemStatus);
        }

        return itemStatus.includes(filterStatus) || itemStatus === filterStatus;
      })
      .filter(item => (filtros.bank ? item.bank === filtros.bank : true))
      .filter(item => (filtros.method ? item.type === filtros.method : true))
      .filter(item => (filtros.ultimos4 ? (item.last_four_digits || '').endsWith(filtros.ultimos4) : true))
      .filter(item => (filtros.transaccionId ? String(item.id).includes(filtros.transaccionId) : true))
      .filter(item => {

        if (!filtros.fechaInicio && !filtros.fechaFin) return true;


        const rawDate = item.created_at || item.launch_time;
        if (!rawDate) return true;

        const itemDate = moment(rawDate);
        if (!itemDate.isValid()) return true;

        let start = null;
        let end = null;

        if (filtros.fechaInicio) {
          const timeStr = filtros.horaInicio || "00:00";
          start = moment(`${filtros.fechaInicio} ${timeStr}`, "YYYY-MM-DD HH:mm");
        }

        if (filtros.fechaFin) {
          const timeStr = filtros.horaFin || "23:59";
          end = moment(`${filtros.fechaFin} ${timeStr}`, "YYYY-MM-DD HH:mm").endOf('minute');
        }

        const itemTime = itemDate.valueOf();

        if (start && start.isValid() && end && end.isValid()) {
          return itemTime >= start.valueOf() && itemTime <= end.valueOf();
        }
        if (start && start.isValid()) {
          return itemTime >= start.valueOf();
        }
        if (end && end.isValid()) {
          return itemTime <= end.valueOf();
        }
        return true;
      });

    const totalPagesCalc = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filtered.slice(startIndex, endIndex);

    const totalVentasPagina = filtered
      .filter(item => {
        if (!item.status) return false;
        const normalizedStatus = item.status.toLowerCase();
        return normalizedStatus === "approvedtransaction" || normalizedStatus === "aprobada" || normalizedStatus === "approved";
      })
      .reduce((sum, item) => sum + (Number(item.trade_profit) || 0), 0);

    const banks = [...new Set(data.map(r => r.bank).filter(Boolean))];
    const methods = [...new Set(data.map(r => r.type).filter(Boolean))];

    return {
      currentItems,
      totalPages: totalPagesCalc || 1,
      totalVentasPagina,
      uniqueBanks: banks.map(b => ({ value: b, label: b })),
      uniqueMethods: methods.map(m => ({ value: m, label: m })),
    };
  }, [data, filtros, currentPage]);

  const limpiarFiltros = () => {
    setFiltros({
      status: "",
      bank: "",
      method: "",
      ultimos4: "",
      transaccionId: "",
      fechaInicio: "",
      horaInicio: "",
      fechaFin: "",
      horaFin: "",
    });
    setCurrentPage(1);
  };

  const handleInputChange = (e, key) => {
    setFiltros(prev => ({ ...prev, [key]: e.target.value }));
    setCurrentPage(1);
  };

  const handleSelectChange = (value, key) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  return {
    ...memoizedData,
    loading,
    error,
    filtros,
    currentPage,
    showApproved,
    selectedColumns,
    setSelectedColumns,
    setShowApproved,
    handleInputChange,
    handleSelectChange,
    limpiarFiltros,
    handlePageChange
  };
}; 